import type { ParticipantListOrder } from "../types/participantListOrder";
import { firestoreService } from "./firestoreService";
import { functionsService, PublicRacerProfile, RegisteredRacer } from "./functionsService";
import { racerDataCache } from "./racerDataCache";
import type { Race, UserRace } from "../types/iosModels";

export type RaceField = { race: Race | null; racers: RegisteredRacer[]; nextCursor: string | null; totalCount: number; categories: string[] };
export { isRacerAccessFailure } from "../utils/racerAccessFailure";
const fieldKey = (raceId: string, viewerUserId: string | undefined, order: ParticipantListOrder, locale: string, category?: string) => JSON.stringify([viewerUserId, "field", raceId, order, locale, category]);
const profileKey = (userId: string, viewerUserId?: string) => JSON.stringify([viewerUserId, "profile", userId]);

export const racerDataService = {
  peekField(raceId: string, viewerUserId?: string, order: ParticipantListOrder = "registrationDate", locale = "en", category?: string) { return racerDataCache.get<RaceField>(fieldKey(raceId, viewerUserId, order, locale, category)); },
  fetchField(raceId: string, viewerUserId?: string, order: ParticipantListOrder = "registrationDate", locale = "en", category?: string): Promise<RaceField> {
    return racerDataCache.load(fieldKey(raceId, viewerUserId, order, locale, category), async () => {
      const [race, page] = await Promise.all([
        firestoreService.fetchRaceById(raceId),
        functionsService.getRegisteredRacers({ raceId, pageSize: 20, order, locale, ...(category ? { category } : {}) }),
      ]);
      return { race, ...page };
    });
  },
  async fetchMore(raceId: string, field: RaceField, viewerUserId?: string, order: ParticipantListOrder = "registrationDate", locale = "en", category?: string): Promise<RaceField> {
    if (!field.nextCursor) return field;
    const key = fieldKey(raceId, viewerUserId, order, locale, category);
    const generation = racerDataCache.generation;
    const page = await racerDataCache.load(`${key}:${field.nextCursor}`, () =>
      functionsService.getRegisteredRacers({ raceId, pageSize: 20, cursor: field.nextCursor!, order, locale, ...(category ? { category } : {}) }));
    const merged = { ...field, ...page, racers: Array.from(new Map(
      [...field.racers, ...page.racers].map(racer => [racer.id, racer])
    ).values()) };
    // Do not retain results after an auth/privilege invalidation.
    if (racerDataCache.get(key) && racerDataCache.generation === generation) racerDataCache.set(key, merged);
    return merged;
  },
  peekProfile(userId: string, viewerUserId?: string) { return racerDataCache.get<PublicRacerProfile>(profileKey(userId, viewerUserId)); },
  fetchProfile(userId: string, viewerUserId?: string): Promise<PublicRacerProfile> {
    const key = profileKey(userId, viewerUserId);
    return racerDataCache.load(key, async () => (await functionsService.getPublicRacerProfile({ userId })).profile);
  },
  // Called only with the current, authorized page; a partial row is never used
  // as a private editable account document.
  seedProfiles(racers: RegisteredRacer[], viewerUserId?: string) {
    racers.forEach(racer => racerDataCache.set(profileKey(racer.id, viewerUserId), racer));
  },
  fetchRaces(userId: string, viewerUserId?: string, raceId?: string): Promise<UserRace[]> {
    // Own attempts also drive registration/check-in/plate UI. Those reads must
    // reflect mutations immediately, rather than a cached empty pre-entry list.
    if (userId === viewerUserId) return firestoreService.fetchUserRaces(userId, raceId);
    const key = JSON.stringify([viewerUserId, "history", userId, raceId]);
    return racerDataCache.load(key, () => functionsService.getPublicRacerRaces({ userId }).then(result => result.races));
  },
  clear: () => racerDataCache.clear(),
};
